<template>
  <b-container id="journal-view-container">
    <b-row align-v="center" align-h="center">
      <b-col cols="12" md="8">

        <div class="panel--invisible" id="create-entry">
          <h1 class="text-center">{{ $t('journal:list') }}</h1>
          <div class="text-center">
            <h5 v-if="!journalLoading && (!journal || journal.length === 0)">
              {{ $t('journal:no-entries') }}
            </h5>
            <CreateJournalEntry ref="from"/>
            <b-btn id="add-new-entry" class="mt-3 mb-4" variant="light" v-b-modal.modal-create-entry>
            <icon name="pen" style="font-size: 12px; opacity: 0.6; vertical-align: baseline;"/> {{ $t('journal:add-new') }}
            </b-btn>
          </div>

          <transition-group name="journal-animation" tag="div" id="journals-view">
            <b-card id="journal-list-item" class="journal-list-item text-left mb-3" v-for="entry in journal" v-bind:key="entry._id" @click="openedEntry = entry" v-b-modal.modal-view-entry>
              <div class="entry-created-date m-1">{{ entry.createdAt | moment('from') }}</div>
              <p id="journal-entry-item" class="journal-entry-preview m-1">{{ entry.content }}</p>
            </b-card>
          </transition-group>
        </div>

        <b-modal id="modal-view-entry" ref="journalModal" :hide-footer="true" scrollable v-if="openedEntry" no-stacking>
          <div class="edit-delete-button">
            <b-button id="delete-journal-entry-button" class="float-right mx-1" type="button" size="sm" variant="outline-danger" v-b-modal.modal-delete-entry>
              <icon name="trash" style="font-size: 12px; opacity: 0.6; vertical-align: baseline;"/>
              {{ $t('journal:modal-delete') }}
            </b-button>
            <b-button id="edit-journal-entry-button" class="float-right mx-1" type="button" size="sm" variant="outline-success" v-on:click="updateEntry(openedEntry)" v-b-modal.modal-edit-entry>
              <icon name="pen" style="font-size: 12px; opacity: 0.6; vertical-align: baseline;"/>
              {{ $t('journal:modal-edit') }}
            </b-button>
          </div>

          <div class="text-center">
            <b-button-group size="sm" class="py-2">
              <b-button class="arrow-button" type="button" variant="link" v-on:click="nextEntry(openedEntry)" v-b-modal.modal-view-entry>
                <icon name="arrow-left" style="font-size: 12px; opacity: 0.6; vertical-align: baseline;"/>
              </b-button>

              <h4 id="journal-entry-timestamp" class="journal-entry-timestamp mx-1">
                {{ openedEntry.createdAt | moment('calendar') }}
              </h4>

              <b-button class="arrow-button" type="button" variant="link" v-on:click="prevEntry(openedEntry)" v-b-modal.modal-view-entry>
                <icon name="arrow-right" style="font-size: 12px; opacity: 0.6; vertical-align: baseline;"/>
              </b-button>
            </b-button-group>
          </div>
          <div id="opened-entry">
            <div v-if="openedEntry.challenge" class="related-challenge text-center mb-3" @click="$router.push({ name: 'Challenge', params: { themeId: openedEntry.challenge.themeId, challengeId: openedEntry.challenge.challengeId }})">
              <div v-if="openedEntry.challenge.data">
                <div class="tag">{{ $t('challenges:' + openedEntry.challenge.data.type) }}</div>
                <p class="mt-2">{{ openedEntry.challenge.data.title }} – {{openedEntry.challenge.data.pages.find(p => p.id === openedEntry.challenge.challengePageId).title}}</p>
              </div>
              <div v-else>...</div>
            </div>
            <p class="mt-2">{{ openedEntry.content }}</p>
          </div>

          <div class="text-center py-2">
            <b-button id="close-journal-entry-button" type="button" @click="hideJournalModal" variant="outline-primary">
              {{ $t('journal:modal-close') }}
            </b-button>
          </div>
        </b-modal>

        <b-modal
                id="modal-delete-entry"
                :hide-footer="true"
                ref="deleteModal">

               <p> {{ $t('journal:modal-deleted')  }} </p>

            <div class="text-center py-3 mt-3">
              <b-button class="mx-1" type="button" @click="hideDeleteModal()"  variant="outline-primary">{{ $t('journal:modal-cancel')  }}</b-button>
              <b-button @click="hideDeleteModal()" v-on:click="removeJournalEntry(openedEntry._id)" variant="outline-danger">
                 {{ $t('journal:modal-delete') }}
              </b-button>
            </div>
        </b-modal>

        <b-modal
                id="modal-edit-entry"
                ref="editModal"
                :header-text-variant="$t('journal:modal-enter')"
                :hide-footer="true"
                @shown="focusOnTextarea">
            <h4 class="journal-entry-timestamp mb-3" v-if="editEntryCreatedAt"> {{ editEntryCreatedAt | moment('calendar') }}  </h4>
            <form @submit.stop.prevent="editJournalEntry()">
              <label name="editEntryContent"></label>
              <b-form-textarea
                            ref="textarea"
                            id="editEntryContent"
                            v-model="editEntryContent"
                            :rows="5"
                            :max-rows="8"
                            :maxlength="journalEntryMaxLength">
              </b-form-textarea>
              <div id="char-info" class="character-counter mt-1 mb-0">
                <span id="edit-character-error" v-if="journalEntryMaxLength - editEntryContent.length <= 0">{{ $t('journal:character-limit', { limit: journalEntryMaxLength }) }}</span>
                <span id="edit-characters-left" v-else-if="journalEntryMaxLength - showWarningBeforeMaxLength <= editEntryContent.length">{{ journalEntryMaxLength - editEntryContent.length }} / {{ journalEntryMaxLength }}</span>
              </div>
              <div class="text-center py-3 mt-3">
                <b-button id="cancel-edit-journal-entry-button" class="mx-1" type="button" @click="hideEditModal(); clearEntry();" variant="outline-danger">{{ $t('journal:modal-cancel')  }}</b-button>
                <b-button id="save-edited-journal-entry-button" class="mx-1" type="submit" :disabled="!editEntryValid" @click="hideEditModal" variant="success">{{ $t('journal:modal-save')  }}</b-button>
              </div>
            </form>
        </b-modal>

      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CreateJournalEntry from './createJournalEntry/CreateJournalEntry'

const MIN_CHARACTERS = 0
const MAX_CHARACTERS = 5000

export default {
  name: 'journal',
  data () {
    return {
      journalEntryMaxLength: MAX_CHARACTERS,
      showWarningBeforeMaxLength: 100,
      openedEntry: null,
      editEntryCreatedAt: null,
      editEntryContent: '',
      editEntryId: null
    }
  },
  watch: {
    openedEntry: {
      immediate: false,
      handler: function (newVal, oldVal) {
        if (this.openedEntry && this.openedEntry.challenge && this.openedEntry.challenge.challengeId) {
          this.axios.get('/private/content/challenges/' + this.openedEntry.challenge.challengeId + '/?lang=' + this.$store.state.language).then((response) => {
            this.openedEntry.challenge.data = response.data.challenge
            this.$forceUpdate()
          })
        }
      }
    }
  },
  mounted: function () {
    this.$store.dispatch('loadJournal')
  },
  components: { CreateJournalEntry },
  methods: {
    ...mapActions([
      'removeEntry'
    ]),
    hideModal () {
      this.$refs.creationModal.hide()
    },
    hideJournalModal () {
      this.$refs.journalModal.hide()
    },
    hideDeleteModal () {
      this.$refs.deleteModal.hide()
    },
    hideEditModal () {
      this.$refs.editModal.hide()
    },
    focusOnTextarea () {
      this.$refs.textarea.focus()
    },
    removeJournalEntry: function (id) {
      this.$store.dispatch('removeEntry', id)
    },
    updateEntry: function (entry) {
      this.editEntryCreatedAt = entry.createdAt
      this.editEntryContent = entry.content
      this.editEntryId = entry._id
    },
    editJournalEntry: function () {
      const entryPackage = {
        id: this.editEntryId,
        content: this.editEntryContent
      }
      this.$store.dispatch('editJournalEntry', entryPackage)
    },
    nextEntry: function (openedEntry) {
      const index = this.$store.state.journal.findIndex(item => {
        return item._id === openedEntry._id
      })
      if (index >= 1) {
        this.openedEntry = this.$store.state.journal[index - 1]
      }
    },
    prevEntry: function (openedEntry) {
      const index = this.$store.state.journal.findIndex(item => {
        return item._id === openedEntry._id
      })
      if (index <= this.$store.state.journal.length - 2) {
        this.openedEntry = this.$store.state.journal[index + 1]
      }
    }
  },
  computed: {
    ...mapGetters(['journal', 'journalLoading']),
    editEntryValid: function () {
      if (this.editEntryContent.length === MIN_CHARACTERS) {
        return false
      } else if (this.editEntryContent.length > MAX_CHARACTERS) {
        return false
      } else {
        return true
      }
    }
  }
}
</script>

<style lang="scss">

.journal-entry-timestamp {
  color: #17B5CF;
  text-align: center;
}

.journal-entry-preview {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.journal-list-item {
  color: black;
  cursor: pointer;
  border-color: #17B5CF;
  box-shadow: 0 3px 12px rgba(black, 0.12);
}

.entry-created-date {
  font-size: 0.8em;
  color: #888;
}

#opened-entry {
  white-space: pre-line;
  word-break: break-word;
}

.journal-animation-move {
   transition: all 0.5s ease;
}

.journal-animation-enter-active, .journal-animation-leave-active {
  transition: all 0.5s ease;
}
.journal-animation-enter, .journal-animation-leave-to {
  opacity: 0;
}

.arrow-button {
color: #17B5CF;
}

.edit-delete-button {
 margin-bottom: 32px;
}

.related-challenge {
  cursor: pointer;
  background-repeat: no-repeat;
  background-color: #e3f5f9;
  background-image: url('/img/background-logo.svg'), radial-gradient(circle at center center,rgba(white,0.8), rgba(white,0.6)), radial-gradient(circle at bottom right,#b7e3ef, #9bdbec);
  background-size: cover;
  background-position: center;
  margin: 0 -1rem;
  padding: 1rem;
  color: #156f87;
  .tag {
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 0.8em;
    font-weight: bold;
    color: #20a8cc;
  }
}

</style>
